import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { BotActivity } from '../lib/types';
import { heat } from '../lib/botPage';
import { fmtIct } from '../lib/ict';
import { WARN_TEXT, HEAT } from '../lib/brand';

const SHADES = HEAT;

/** Work events by hour for the last 7 days and by day for 30 days. Days before the team's first recorded event show "no data", never 0. */
export function Heartbeat({ activity }: { activity?: BotActivity | null }) {
  if (!activity) return <Typography variant="body2" color="text.secondary">No activity data for this bot.</Typography>;
  const hours = activity.events_by_hour_7d || [];
  const days = activity.events_by_day || [];
  const maxHour = Math.max(0, ...hours.flatMap((d) => d.hours || []));
  const maxDay = Math.max(0, ...days.map((d) => d.events || 0));
  return (
    <Box data-testid="heartbeat">
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.6 }}>
        Last 7 days by hour (ICT) · {activity.events_7d ?? 'no data'} events · {activity.events_30d ?? 'no data'} in 30 days · {activity.sessions_30d ?? 'no data'} sessions
      </Typography>
      <Box role="img" aria-label="Work events by hour, last 7 days" sx={{ display: 'grid', gridTemplateColumns: '64px repeat(24, minmax(0, 1fr))', gap: '2px', alignItems: 'center' }}>
        {hours.map((d) => (
          <React.Fragment key={d.date}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{d.date.slice(5)}</Typography>
            {Array.from({ length: 24 }, (_, h) => {
              const n = d.hours ? d.hours[h] : null;
              const label = d.hours ? `${d.date} ${String(h).padStart(2, '0')}:00 ICT: ${n} event${n === 1 ? '' : 's'}` : `${d.date}: no data`;
              return (
                <Tooltip key={h} title={label} arrow describeChild>
                  <Box data-cell={d.hours ? heat(n, maxHour) : 'nodata'} sx={{ height: 12, borderRadius: '2px', bgcolor: d.hours ? SHADES[heat(n, maxHour)] : 'transparent', border: d.hours ? 'none' : '1px dashed', borderColor: 'divider' }} />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.2, mb: 0.4 }}>Last 30 days</Typography>
      <Box role="img" aria-label="Work events by day, last 30 days" sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 44 }}>
        {days.map((d) => (
          <Tooltip key={d.date} title={d.events == null ? `${d.date}: no data` : `${d.date}: ${d.events} event${d.events === 1 ? '' : 's'}`} arrow describeChild>
            <Box sx={{ flex: 1, minWidth: 2, height: d.events == null ? 3 : Math.max(3, maxDay ? (d.events / maxDay) * 44 : 3), bgcolor: d.events == null ? 'divider' : SHADES[4], opacity: d.events == null ? 0.5 : 1, borderRadius: '2px' }} />
          </Tooltip>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        Last work {fmtIct(activity.last_work_at)} · idle for {activity.idle_for || 'no data'}
        {activity.cadence_hours != null ? ` · usual gap ${activity.cadence_hours} h` : ''}
        {activity.jd_cadence ? ` · JD says ${activity.jd_cadence.keyword}` : ''}
      </Typography>
      {activity.idle_alert && <Typography variant="caption" sx={{ color: WARN_TEXT, fontWeight: 700 }} component="div">⚠ {activity.idle_alert}</Typography>}
      {activity.basis && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.4 }}>{activity.basis}</Typography>}
    </Box>
  );
}
